import { Box, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { FeatureOptionsRenderer } from '@/console/features/jobs/components/feature-options-renderer/feature-options-renderer';
import { JobExecutionId } from '@/console/features/jobs/components/job-execution-id/job-execution-id';
import { JobIdLabel } from '@/console/features/jobs/components/job-id-label/job-id-label';
import { JobProject } from '@/console/features/jobs/components/job-project/job-project';
import { JobProjectVersion } from '@/console/features/jobs/components/job-project-version/job-project-version';
import { JobSource } from '@/console/features/jobs/components/job-source/job-source';
import { TJobRenderComponentProps } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.types';
import { Avatar } from '@/shared/components/ui/avatar/avatar';
import { Button } from '@/shared/components/ui/button/button';
import { ResourceDetailContent } from '@/shared/components/ui/resource-detail/content/resource-detail-content';
import { TResourceDetailItem } from '@/shared/components/ui/resource-detail/item/resource-detail-item.types';
import { getTargetJobIdFromMetaData } from '@/shared/features/jobs/jobs.utils';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { formatDuration } from '@/shared/utils/format';

export const JobDetails: React.FC<TJobRenderComponentProps> = ({ job }) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();

  const targetJobId = getTargetJobIdFromMetaData(job?.metaData, job?.jobType!);
  const restoreJobId = job?.metaData?.restore_job_id;

  const copyToClipboard = async () => {
    if (!job?.jobId) {
      return;
    }
    await navigator.clipboard.writeText(job?.jobId);
    toast.success(t('common:copied_to_clipboard'));
  };

  const details: TResourceDetailItem[] = [
    {
      value: (
        <Button
          tooltip={job?.jobId}
          size="small"
          variant="secondary"
          onClick={copyToClipboard}
        >
          {t('pipelines:copy_job_id')}
        </Button>
      ),
    },
    {
      label: t('common:triggered_by'),
      value: <Avatar user={job?.creationUser} />,
    },
    {
      label: t('common:duration'),
      value: formatDuration(t, job?.duration),
    },
    {
      label: t('common:finished_at'),
      value: formatLocalTime(job?.finishedTimestamp),
    },
  ];

  if (targetJobId) {
    details.push({
      label: t('pipelines:target_job'),
      value: <JobIdLabel jobType={job?.jobType!} jobId={targetJobId} />,
    });
  }

  if (restoreJobId) {
    details.push({
      label: t('pipelines:restored_from_job'),
      value: (
        <JobIdLabel
          jobType={job?.jobType!}
          jobId={restoreJobId}
          variant="restore"
        />
      ),
    });
  }

  if (job?.source) {
    details.push(
      {
        label: t('common:source'),
        value: <JobSource job={job} truncate />,
      },
      {
        label: job?.pipelineExecutionId ? t('pipelines:execution_id') : '',
        value: <JobExecutionId job={job} />,
      },
    );
  }

  if (job?.projectIds?.[0]) {
    details.push(
      {
        label: t('common:project'),
        value: <JobProject job={job} truncate />,
      },
      {
        label: t('common:version'),
        value: <JobProjectVersion job={job} />,
      },
    );
  }

  return (
    <Stack gap="2rem">
      <Box margin="-0.5rem -1.5rem -1rem">
        <ResourceDetailContent items={details} />
      </Box>
      <FeatureOptionsRenderer job={job} />
    </Stack>
  );
};
